import { Utils, StringUtils } from "neo-common";
import { Communication, Platforms } from "neo-games";
import { TweenLite, TweenMax } from "gsap";
import { GsapEffects } from "../../../Components/Effects/GsapEffects";
import { GameManager } from "../../../Managers/GameManager";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../../Utils/FrameTimer";
import { CustomEase } from "../../../Components/Effects/CustomEase";
import { ModuleManager } from "../../../Managers/ModuleManager";
import { Jackpot } from "../Bonuses/Jackpot";
import { HtmlController } from "../../../HTMLComponents/HtmlController";
import { IProgressiveBonusLevel } from "neo-games/Engine/Communication/Game/Types/IProgressiveBonusLevel";
import { BalanceManager } from "../../../Managers/BalanceManager";
import { GameData } from "../Datas/GameData";
import { DataManager } from "../../../Managers/DataManager";
import { FrameTimerPool } from "../../../Utils/FrameTimerPool";
import { StageController } from "../../../Components/StageController";
import { AppContext } from "../../../../Common/AppContext";
import { PlayList } from "../../../Components/Sounds/PlayList";
import { AmbientBackground } from "../../../Utils/AmbientBackground";
import { Languages } from "../../../Utils/Languages";
import { MessageManager } from "../../../Managers/MessageManager";
import { JackpotData } from "../Datas/JackpotData";
import { LimitDialog } from "../Dialogs/LimitDialog";

var GameModules = Communication.Game.GameModules;

type MaxPowerBonusInitResponse = Communication.Game.MaxPowerBonusInitResponse;
type MaxPowerBonusSpinResponse = Communication.Game.MaxPowerBonusSpinResponse;

export class JackpotGame {
    //******************************************************************************************************************
    //  SEETTINGS
    //******************************************************************************************************************

    private static readonly Strip: Array<number> = [
        1, 1, 1, 1, 2, 1, 1, 3, 1, 1, 1, 2, 2, 1, 1, 1, 1, 1, 2, 1, 4, 1, 2, 1, 1, 2, 1, 3, 1, 1, 2, 1, 1, 1, 2, 1, 1,
        1, 1, 3, 1, 1, 1, 2, 1, 2, 1, 1, 1, 1, 4,
    ];

    private static readonly SymbolsCount: number = 4;

    private static readonly BuzzerDuration: number = 180; //in frames

    private static readonly SpinRollSpeed: number = 8; //lower number is faster

    private static readonly MinSpinDuration: number = 360; //in frames

    private static readonly MiddleSymbolIndex: number = 1;

    private static readonly SpinPreRollDuration: number = 120;

    private static readonly SpinPreRollSymbolCount: number = 8;

    private static readonly SpinPostRollDuration: number = 360;

    private static readonly SpinPostRollSymbolCount: number = 12;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected ticket: { id: string };

    protected gameInitialized: boolean;

    protected gameStarted: boolean;

    protected autostart: boolean;

    protected callback: () => void;

    protected holder: HTMLElement;

    protected root: HTMLElement;

    protected reel: HTMLElement;

    protected spinBtn: HTMLElement;

    protected winContainer: HTMLElement;

    protected gameData: MaxPowerBonusSpinResponse;

    protected lastEvent: () => void;

    protected stoppedFromMachine: boolean;

    protected stoppedFromServer: boolean;

    protected stopTimer: FrameTimerEventEmitter;

    protected isStopping: boolean;

    protected currentSymbols: Array<HTMLElement>;

    protected currentSymbolsHeight: Array<number>;

    protected symbolStepOffsets: Array<number>;

    protected symbolsHeight: Array<number>;

    protected stripIndex: number;

    protected rootHeight: number;

    protected currentOffset: number;

    protected showHandPayDialog: boolean;

    protected buzzerTimer: FrameTimerEventEmitter;

    protected showWinDelay: FrameTimerEventEmitter;

    protected assetsLoaded: boolean;

    protected startAfterLoad: boolean;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor(holder: HTMLElement) {
        this.holder = holder;

        this.root = this.createBaseLayout();
        this.holder.appendChild(this.root);

        let intro = this.createAnimations();
        this.holder.appendChild(intro);

        this.stopTimer = CreateFrameTimer("MiniGameSpinStop");
        this.stopTimer.on(FrameTimerEvents.Complete, this.spinStop.bind(this));

        this.buzzerTimer = CreateFrameTimer("BuzzerTimer");
        this.buzzerTimer.on(FrameTimerEvents.Complete, this.buzzerTimeFinished.bind(this));

        this.showWinDelay = CreateFrameTimer("BuzzerTimer");
        this.showWinDelay.on(FrameTimerEvents.Complete, this.updateWin.bind(this));

        this.gameInitialized = false;
        this.gameStarted = false;
        this.autostart = false;
        this.showHandPayDialog = false;
        this.assetsLoaded = false;
        this.startAfterLoad = false;

        // CustomEase.create("miniGameSpinStopEase", "M0,0,C0,0,0.09,0.494,0.652,0.862,0.844,0.987,1,1,1,1");
        CustomEase.create(
            "miniGameSpinStopEase",
            "M0,0,C0,0,0.212,0.616,0.452,0.842,0.519,0.905,0.627,1.008,0.716,1.026,0.768,1.036,0.819,1.031,0.848,1.028,0.89,1.022,1,1,1,1",
        );
    }

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    protected createBaseLayout(): HTMLElement {
        let root = this.createElement("div", "jackpotGame");

        this.reel = this.createElement("div", "jackpotReel", root);

        this.createElement("div", "jackpotTextOverlay", root);

        let hintContainer = this.createElement("div", "jackpotStartHint", root);
        this.createTextElement("div", "jackpotStartHintText", Languages.getRid("PB_MINIGAME_SPINSTART"), hintContainer);
        this.createTextElement("div", "jackpotStartHintText", Languages.getRid("PB_MINIGAME_SPINSTART"), hintContainer);

        this.createElement("div", "jackpotFrameLeft", root);
        this.createElement("div", "jackpotFrameRight", root);
        this.createElement("div", "jackpotGlowLeft", root);
        this.createElement("div", "jackpotGlowRight", root);

        let arrows = this.createElement("div", "jackpotArrows", root);
        let arrow = this.createElement("div", "jackpotArrowLeft", arrows);
        this.createElement("div", "jackpotArrowBlur", arrow);
        arrow = this.createElement("div", "jackpotArrowRight", arrows);
        this.createElement("div", "jackpotArrowBlur", arrow);

        this.createTextElement("div", "jackpotGameName", document.getElementById("gameTitle").innerHTML, root);

        return root;
    }

    protected createAnimations(): HTMLElement {
        let intro = this.createElement("div", "jackpotIntro");

        for (let i = 0; i < 5; i++) {
            let firework = this.createElement("div", "jackpotFireworks", intro);
            for (let j = 0; j < 8; j++) {
                this.createElement("div", "fireworksPart", firework);
            }
        }

        let titles = this.createElement("div", "jackpotTitles", intro);
        let _self = this;

        document.body.classList.add("jpt-loading");

        //dynamic content loader
        Promise.all<Array<string>>([
            this.getCustomContent("jackpotTextSynot", ".png"),
            this.getCustomContent("jackpotTextProgressive", ".png"),
            this.getCustomContent("jackpotTextBonus", ".png"),
        ]).then((values: Array<Array<string>>) => {
            for (let i = 0; i < values.length; i++) {
                let elem = _self.createElement("div", values[i][0], titles);

                if (values[i][1] != "") {
                    elem.style.background = `url(${values[i][1]}) no-repeat`;
                }
            }

            document.body.classList.remove("jpt-loading");
            _self.assetsLoaded = true;
            if (_self.startAfterLoad) {
                _self.intro();
            }
        });

        this.createElement("div", "jackpotTextOverlay", intro);
        this.winContainer = this.createElement("div", "jackpotWinValue", intro);

        return intro;
    }

    //******************************************************************************************************************
    //  HANDLE GAME
    //******************************************************************************************************************

    public init(autostart: boolean, callback: () => void): void {
        if (this.ticket == null && GameManager.gameResponse && GameManager.gameResponse["progressiveBonus"].ticket) {
            this.ticket = GameManager.gameResponse["progressiveBonus"].ticket;

            this.gameInitialized = false;
            this.autostart = autostart;
            this.callback = callback;
            this.initSymbols();
            this.createSymbols();
            this.sendInitBonusGameRequest();
        }
    }

    private initFinished(): void {
        this.gameInitialized = true;

        if (this.autostart) {
            if (this.callback != null) {
                this.callback();
            }

            this.intro();
        }
    }

    public start(): void {
        if (!this.autostart) {
            this.intro();
        }
    }

    private startGame(): void {
        //close all opened dialogs
        HtmlController.dispatchAction("");

        Jackpot.refreshBonusInfoBeforeSpin(this.sendSpinBonusGameRequest.bind(this));
        this.spinStart();
    }

    private spinStop(fromMachine: boolean = true, fromServer: boolean = false): void {
        if (fromMachine) {
            this.stoppedFromMachine = true;
        }

        if (fromServer) {
            this.stoppedFromServer = true;
        }

        if (this.stoppedFromMachine && this.stoppedFromServer) {
            this.isStopping = true;
        }
    }

    private closeMiniGame(): void {
        if (this.callback != null && this.autostart) {
            this.callback();
        }

        this.outroFinished(false);
    }

    private setBaseGameSound(enable: boolean): void {
        ModuleManager.toggleModuleSounds(GameModules.Base, enable);
    }

    public hasInitializedGame(): boolean {
        return this.gameInitialized;
    }

    public hasStartedGame(): boolean {
        return this.gameStarted;
    }

    public disableAutostart(): void {
        this.autostart = false;

        //clear response to allow realitycheck on spin
        this.clearJackpotGameResponse();
    }

    //******************************************************************************************************************
    //  CLEAR
    //******************************************************************************************************************

    private clearJackpotGame(): void {
        if (this.holder) {
            this.holder.classList.remove("outro");
            this.holder.classList.add("_hidden");
            this.holder.className = "jackpotGameHolder _hidden";
        }

        if (this.root) {
            this.root.removeEventListener("webkitAnimationEnd", this.lastEvent);
            this.root.removeEventListener("animationend", this.lastEvent);
        }

        if (this.reel) {
            this.reel.innerHTML = "";

            this.currentOffset = 0;
            this.setTransformY(this.reel, this.currentOffset);
            this.currentSymbols = [];
            this.currentSymbolsHeight = [];
        }

        if (this.winContainer) {
            this.winContainer.style.transform = "scale(0)";
            this.winContainer.innerHTML = "";

            this.winContainer.removeEventListener("webkitAnimationEnd", this.lastEvent);
            this.winContainer.removeEventListener("animationend", this.lastEvent);
        }

        this.ticket = null;
    }

    private clearJackpotGameResponse(): void {
        if (GameManager.gameResponse["progressiveBonus"]) {
            GameManager.gameResponse["progressiveBonus"] = null;
        }
    }

    //******************************************************************************************************************
    //  REQUESTS & RESPONSE
    //******************************************************************************************************************

    private sendInitBonusGameRequest(): void {
        if (GameManager.gameClient) {
            GameManager.gameClient
                .sendMaxPowerBonusInitRequest(this.ticket)
                .then((response: MaxPowerBonusInitResponse) => {
                    if (response != null && response.status == "Active") {
                        this.initFinished();
                    } else {
                        this.closeMiniGame();
                    }
                });
        }
    }

    private sendSpinBonusGameRequest(): void {
        if (GameManager.gameClient) {
            GameManager.gameClient
                .sendMaxPowerBonusSpinRequest(this.ticket)
                .then((response: MaxPowerBonusSpinResponse) => {
                    this.setGameData(response);
                });
        }
    }

    private setGameData(response: MaxPowerBonusSpinResponse): void {
        if (response != null) {
            this.gameData = response;
            this.spinStop(false, true);

            if (AppContext != null && AppContext.gameConfiguration.GameRoundIdVisible) {
                HtmlController.updateDataElement(GameData.DataRoundId, this.ticket.id);
            }
        } else {
            Jackpot.showError();
        }
    }

    //******************************************************************************************************************
    //  INTRO & OUTRO
    //******************************************************************************************************************

    private intro(): void {
        if (!this.assetsLoaded) {
            this.startAfterLoad = true;
            return;
        }
        if (!this.gameStarted) {
            this.gameStarted = true;

            PlayList.loop("SND_JackpotBuzzer");
            this.bounceCounters(true);

            this.buzzerTimer.stop();
            this.buzzerTimer.start(JackpotGame.BuzzerDuration);

            //intro calls preparenewgameround before baseview intro
            //timeout will call method after baseview intro
            setTimeout(() => {
                this.setBaseGameSound(false);
            }, 1);
        }
    }

    private buzzerTimeFinished(): void {
        this.buzzerTimer.stop();
        this.bounceCounters(false);
        PlayList.stop("SND_JackpotBuzzer");
        LimitDialog.closeDialog();
        DataManager.GameLimit = false; //avoid restoregameround open limit dialog again

        if (this.holder) {
            this.lastEvent = this.introFinished.bind(this);
            this.root.addEventListener("webkitAnimationEnd", this.lastEvent);
            this.root.addEventListener("animationend", this.lastEvent);

            AmbientBackground.changeAmbientBackground("jackpotBg", 1000, 5500);

            PlayList.play("SND_JackpotIntroHit");
            PlayList.loop("SND_JackpotIntroWaitingLoop", 360);

            this.holder.classList.remove("_hidden");
            void this.holder.offsetWidth;
            this.holder.classList.add("start");
        }
    }

    private introFinished(): void {
        if (this.holder) {
            this.root.removeEventListener("webkitAnimationEnd", this.lastEvent);
            this.root.removeEventListener("animationend", this.lastEvent);

            PlayList.stop("SND_JackpotIntroHit");
            this.holder.classList.add("startFinished");

            this.setStageVisibility(false);
            this.createSpinBtn();
        }
    }

    private outro(): void {
        this.setStageVisibility(true);

        this.holder.classList.remove("spin");
        void this.holder.offsetWidth;
        this.holder.classList.add("outro");

        AmbientBackground.changeAmbientBackground("base", 2000, 8000);

        //fire end event after winvalue animation end
        this.lastEvent = this.outroFinished.bind(this);
        this.winContainer.addEventListener("webkitAnimationEnd", this.lastEvent);
        this.winContainer.addEventListener("animationend", this.lastEvent);
    }

    private outroFinished(collect: boolean = true): void {
        if (collect) {
            this.updateBalance();
        }

        this.clearJackpotGame();
        this.clearJackpotGameResponse();

        this.setBaseGameSound(true);

        BalanceManager.refreshBalance();

        if (this.showHandPayDialog) {
            this.openHandPayDialog();
        } else {
            this.restoreGameRound(collect);
        }

        this.autostart = false;
    }

    private updateBalance(): void {
        if (this.gameData != null) {
            if (this.gameData.balance != null) {
                this.showHandPayDialog = false;
                BalanceManager.balanceReceived(this.gameData.balance);
                PlayList.play("SND_BaseCollect");

                if (this.gameData.game != null && this.gameData.game.win != null) {
                    DataManager.updateTotalGain(false, this.gameData.game.win.amount);
                }

                if (AppContext != null && !AppContext.gameConfiguration.CollectOnUnCalculatedWin) {
                    this.updateWin(false, "0");
                }
            } else {
                this.showHandPayDialog = true;
            }
        }
    }

    private restoreGameRound(collect: boolean = true): void {
        this.gameInitialized = false;
        this.gameStarted = false;
        this.showHandPayDialog = false;
        ModuleManager.restoreGameRound(GameModules.Base, collect);
    }

    private openHandPayDialog(): void {
        let response = {
            messages: [
                {
                    text: Languages.getRid(
                        "HANDPAY_DIALOG",
                        Languages.formatText(Languages.FormatMoney, this.gameData.game.win.amount.toString()) +
                            " " +
                            DataManager.getDataById(GameData.DataCurrency),
                    ),
                },
            ],
        };
        MessageManager.postpone(this.restoreGameRound.bind(this), response);
    }

    //******************************************************************************************************************
    //  SHOW & UPDATE SYMBOLS
    //******************************************************************************************************************

    private initSymbols(): void {
        if (this.reel) {
            this.currentSymbols = [];
            this.currentSymbolsHeight = [];
            // this.stripIndex = Math.floor(Math.random() * JackpotGame.Strip.length);
            this.stripIndex = (JackpotGame.Strip.indexOf(4) + 2 + JackpotGame.Strip.length) % JackpotGame.Strip.length; //start with diamond symbols

            //get rootHeight
            if (!this.rootHeight) {
                this.rootHeight = this.getElementHeight(this.root);
            }

            let symbolId: number;

            //getSymbolsHeight
            this.symbolsHeight = [];
            for (let i = 0; i < JackpotGame.SymbolsCount; i++) {
                symbolId = i + 1;
                let symbol = this.createElement("div", "jackpotSymbol l-" + symbolId, this.reel);
                this.symbolsHeight.push(this.getElementHeight(symbol));

                this.reel.removeChild(symbol);
            }
        }
    }

    private createSymbols(): void {
        if (this.reel) {
            //add start symbols queue
            let symbolId: number;
            for (let i = 0; i < JackpotGame.SpinPreRollSymbolCount; i++) {
                this.stripIndex = (this.stripIndex - 1 + JackpotGame.Strip.length) % JackpotGame.Strip.length;
                symbolId = JackpotGame.Strip[this.stripIndex];
                this.addSymbolToReel(symbolId, false, i > 3);
            }

            this.currentOffset = this.getReelOffset(this.currentSymbols.length - 2);
            this.setTransformY(this.reel, this.currentOffset);
        }
    }

    private updateSymbols(): void {
        //clear number of elements to 3
        while (this.currentSymbols.length > 3) {
            this.currentSymbolsHeight.pop();
            let symbol = this.currentSymbols.pop();
            this.reel.removeChild(symbol);
        }

        if (this.isStopping) {
            this.updatePostRollSymbols();
        } else {
            this.stripIndex = (this.stripIndex - 1 + JackpotGame.Strip.length) % JackpotGame.Strip.length;
            let symbolId = JackpotGame.Strip[this.stripIndex];
            this.addSymbolToReel(symbolId, false, true);

            this.currentOffset = this.getReelOffset(this.currentSymbols.length - 2);
            this.setTransformY(this.reel, this.currentOffset);

            this.spinRoll();
        }
    }

    private updatePostRollSymbols(): void {
        //get finish strip index
        if (this.gameData != null) {
            let winSymbolId = this.gameData.game.symbols[JackpotGame.MiddleSymbolIndex];
            let finishStripIndex = Math.floor(Math.random() * JackpotGame.Strip.length);

            //check if finished symbol is our win symbol, if not try next
            while (JackpotGame.Strip[finishStripIndex] != winSymbolId) {
                finishStripIndex = (finishStripIndex - 1 + JackpotGame.Strip.length) % JackpotGame.Strip.length;
            }

            //set new strip index to finishStripIndex + postroll symbols - 1
            this.stripIndex =
                (finishStripIndex + (JackpotGame.SpinPostRollSymbolCount - 1) + JackpotGame.Strip.length) %
                JackpotGame.Strip.length;

            for (let i = 0; i < JackpotGame.SpinPostRollSymbolCount; i++) {
                this.stripIndex = (this.stripIndex - 1 + JackpotGame.Strip.length) % JackpotGame.Strip.length;
                let symbolId = JackpotGame.Strip[this.stripIndex];
                this.addSymbolToReel(symbolId, i == JackpotGame.SpinPostRollSymbolCount - 2, i < 8);
            }

            this.currentOffset = this.getReelOffset(this.currentSymbols.length - 2);
            this.setTransformY(this.reel, this.currentOffset);

            this.spinPostRoll();
        }
    }

    private removeOffScreenSymbols(): void {
        //clear number of elements to 3
        while (this.currentSymbols.length > 3) {
            this.currentSymbolsHeight.pop();
            let symbol = this.currentSymbols.pop();
            this.reel.removeChild(symbol);
        }
    }

    //******************************************************************************************************************
    //  SPIN
    //******************************************************************************************************************

    private spinStart(): void {
        //remove spin btn
        if (this.spinBtn) {
            this.spinBtn.parentElement.removeChild(this.spinBtn);
        }

        document.removeEventListener("keyup", this.lastEvent);

        this.updateWin(false, "0");

        PlayList.stop("SND_JackpotIntroWaitingLoop");
        PlayList.play("SND_JackpotStartHit");
        PlayList.play("SND_JackpotWheelMusic");

        this.stoppedFromMachine = false;
        this.stoppedFromServer = false;

        if (this.holder) {
            this.holder.classList.remove("start");
            this.holder.classList.remove("startFinished");
            this.holder.classList.add("spin");
        }

        this.isStopping = false;
        this.stopTimer.start(JackpotGame.MinSpinDuration);
        this.spinPreRoll();
    }

    private spinPreRoll(): void {
        let nextOffset = this.getReelOffset(1);
        this.symbolStepOffsets = this.getSymbolStepOffsets(1);
        this.animate(
            this.currentOffset,
            nextOffset,
            this.updateSymbols,
            JackpotGame.SpinPreRollDuration,
            "Power2.easeIn",
            0,
            <() => void>this.onSpinPostRollUpdate,
        );
    }

    //******************************************************************************************************************
    //  ROLL
    //******************************************************************************************************************

    private spinRoll(): void {
        let nextOffset = this.getReelOffset(1);
        let duration = this.getSpinRollDuration(nextOffset);

        this.symbolStepOffsets = this.getSymbolStepOffsets(1);
        this.animate(
            this.currentOffset,
            nextOffset,
            this.updateSymbols,
            duration,
            "linear",
            0,
            <() => void>this.onSpinPostRollUpdate,
        );
    }

    //******************************************************************************************************************
    //  POST ROLL
    //******************************************************************************************************************

    private spinPostRoll(): void {
        Jackpot.refreshBonusInfoInMiniGame(this.createLevelDataOverride(false));

        let nextOffset = this.getReelOffset(1);
        this.symbolStepOffsets = this.getSymbolStepOffsets(1);
        this.animate(
            this.currentOffset,
            nextOffset,
            this.spinComplete,
            JackpotGame.SpinPostRollDuration,
            "miniGameSpinStopEase",
            0,
            <() => void>this.onSpinPostRollUpdate,
        );
    }

    private onSpinPostRollUpdate(tween: TweenMax): void {
        if (this.symbolStepOffsets.length > 0) {
            if (tween.target._gsTransform.y > this.symbolStepOffsets[0]) {
                PlayList.play("SND_JackpotWheelClick1");

                if (this.holder) {
                    this.holder.classList.remove("hit");
                    this.holder.offsetWidth;
                    this.holder.classList.add("hit");
                }

                this.symbolStepOffsets.shift();
            }
        }
    }

    //******************************************************************************************************************
    //  COMPLETE
    //******************************************************************************************************************

    private spinComplete(): void {
        //close all opened dialogs
        Platforms.Platforms.closeMenu();

        if (this.gameData != null) {
            this.showWinDelay.start(135);

            if (this.gameData.game.limit) {
                LimitDialog.openDialog();
            }
        }

        this.removeOffScreenSymbols();
        Jackpot.refreshBonusInfoWithWinValue(this.createLevelDataOverride(true));
        let isHigh = this.createWinValue();
        PlayList.stop("SND_JackpotWheelMusic", 30);
        PlayList.play("SND_JackpotWinMusic");

        this.outro();
    }

    private updateWin(collected: boolean, value: string = ""): void {
        this.showWinDelay.stop();
        let uncollectedWin = document.getElementById("uncollectedWin");
        let collectedWin = document.getElementById("collectedWin");

        if (uncollectedWin != null && collectedWin != null) {
            if (!StringUtils.isNullOrEmpty(value)) {
                HtmlController.updateDataElement(GameData.DataTotalWin, value);
            } else {
                HtmlController.updateDataElement(GameData.DataTotalWin, this.gameData.game.win.amount.toString());
            }

            if (collected) {
                uncollectedWin.classList.add("_hidden");
                collectedWin.classList.remove("_hidden");
            } else {
                uncollectedWin.classList.remove("_hidden");
                collectedWin.classList.add("_hidden");
            }
        }
    }

    //******************************************************************************************************************
    //  SPIN ANIMATION
    //******************************************************************************************************************

    private animate(
        currentOffset: number,
        newOffset: number,
        callback: () => void,
        duration: number,
        ease: string = "linear",
        delay: number = 0,
        updateCallback: () => void = null,
    ): void {
        duration = duration * GsapEffects.TickTime;
        delay = delay * GsapEffects.TickTime;

        TweenMax.killTweensOf(this.reel);
        let tween = TweenMax.fromTo(
            this.reel,
            duration,
            { y: currentOffset, roundProps: "y" },
            { y: newOffset, ease: ease, delay: delay, roundProps: "y" },
        );

        this.currentOffset = newOffset;

        if (!Utils.isNullOrUndefined(callback)) {
            tween.eventCallback("onComplete", callback, [], this);
        }

        if (!Utils.isNullOrUndefined(updateCallback)) {
            tween.eventCallback("onUpdate", updateCallback, ["{self}"], this);
        }
    }

    //******************************************************************************************************************
    //  WIN COUNTER
    //******************************************************************************************************************

    public createWinValue(): boolean {
        if (this.gameData != null) {
            let winValue = Languages.formatText(Languages.FormatMoney, this.gameData.game.win.amount.toString());

            if (this.winContainer) {
                let childs = this.winContainer.children;

                for (let i = 0; i < winValue.length; i++) {
                    if (!childs[i]) {
                        this.createElement("div", "n" + winValue.charCodeAt(i).toString(), this.winContainer);
                    } else {
                        childs[i].className = "n" + winValue.charCodeAt(i).toString();
                    }
                }
            }

            return this.gameData.game.win.level == "Gold" || this.gameData.game.win.level == "Diamond";
        }

        return false;
    }

    //******************************************************************************************************************
    //  HELPER
    //******************************************************************************************************************

    public getRoot(): HTMLElement {
        return this.root;
    }

    private getElementHeight(elem: HTMLElement): number {
        return parseInt(window.getComputedStyle(elem).height);
    }

    private getSpinRollDuration(newOffset: number): number {
        let diff = Math.abs(this.currentOffset - newOffset);
        return Math.round((diff / this.symbolsHeight[0]) * JackpotGame.SpinRollSpeed);
    }

    private getReelOffset(middleSymbolIndex: number): number {
        let offset = Math.round(this.rootHeight * 0.5);

        if (this.currentSymbols.length > 0) {
            for (let i = 0; i < middleSymbolIndex; i++) {
                offset -= this.currentSymbolsHeight[i];
            }

            offset -= Math.round(this.currentSymbolsHeight[middleSymbolIndex] * 0.5);
        }

        return offset;
    }

    private getSymbolStepOffsets(middleSymbolIndex: number): Array<number> {
        let offset = this.currentOffset;
        let result: Array<number> = [];

        if (this.currentSymbols.length > 2) {
            offset += this.currentSymbolsHeight[this.currentSymbols.length - 2] * 0.5;
            result.push(offset);

            for (let i = this.currentSymbolsHeight.length - 3; i > middleSymbolIndex; i--) {
                offset += this.currentSymbolsHeight[i];
                result.push(offset);
            }
        }

        return result;
    }

    private createElement(
        tag: string,
        className: string,
        rootElem?: HTMLElement,
        appendBefore: HTMLElement = null,
    ): HTMLElement {
        let elem = document.createElement(tag);
        elem.setAttribute("class", className);

        if (rootElem != null) {
            if (appendBefore != null) {
                rootElem.insertBefore(elem, appendBefore);
            } else {
                rootElem.appendChild(elem);
            }
        }

        return elem;
    }

    private createSymbol(
        tag: string,
        className: string,
        rootElem?: HTMLElement,
        appendBefore: HTMLElement = null,
    ): HTMLElement {
        let elem = document.createElement(tag);
        elem.setAttribute("class", className);

        let symbolHolder = document.createElement(tag);
        symbolHolder.className = "jackpotSymbolHolder";
        elem.appendChild(symbolHolder);

        if (rootElem != null) {
            if (appendBefore != null) {
                rootElem.insertBefore(elem, appendBefore);
            } else {
                rootElem.appendChild(elem);
            }
        }

        return elem;
    }

    //ToDo prerobit na resource ID
    private createTextElement(tag: string, className: string, text: string, rootElem?: HTMLElement): HTMLElement {
        let elem = this.createElement(tag, className, rootElem);
        elem.innerHTML = text;

        return elem;
    }

    private createSpinBtn(): void {
        let userControls = document.getElementById("userControls");
        if (userControls) {
            let spinBtn = userControls.getElementsByClassName("spinBtn")[0];
            if (spinBtn) {
                let parent = spinBtn.parentElement;
                this.spinBtn = this.createElement("div", "btn spinBtn", parent);
                this.spinBtn.innerHTML = spinBtn.innerHTML;
                this.spinBtn.addEventListener("click", this.startGame.bind(this));

                this.lastEvent = this.handleSpaceStart.bind(this);
                document.addEventListener("keyup", this.lastEvent);
            }
        }
    }

    private createLevelDataOverride(winData: boolean): IProgressiveBonusLevel {
        let result = {} as IProgressiveBonusLevel;

        if (this.gameData != null) {
            result.code = this.gameData.game.win.level;

            //to always show minigame win value in counter
            if (winData) {
                result.lastWinAmount = this.gameData.game.win.amount;
            } else {
                result.nextValue = this.gameData.game.win.amount;
                result.nextTimestamp = new Date(new Date().getTime() + 8000).toISOString();
                result.previousValue = this.gameData.game.win.amount;
            }
        }

        return result;
    }

    private setTransformY(elem: HTMLElement, y: number): void {
        elem.style.transform = "translate3d(0, " + y + "px, 0)";
    }

    private setStageVisibility(visible: boolean): void {
        if (StageController.Stage != null) {
            StageController.Stage.visible = visible;
        }
    }

    private handleSpaceStart(event: KeyboardEvent): void {
        if (event.keyCode == 32) {
            this.startGame();
        }
    }

    private addSymbolToReel(symbolId: number, winSymbol: boolean = false, blured: boolean = false): void {
        let className = "jackpotSymbol l-" + symbolId;

        if (winSymbol) {
            className += " jackpotWinSymbol";
            this.holder.classList.add("win-" + symbolId);
        }

        if (blured) {
            className += " blur";
        }

        let symbol = this.createSymbol("div", className, this.reel, this.currentSymbols[0]);
        // symbol.setAttribute('data-symbolId', symbolId.toString());
        this.currentSymbols.unshift(symbol);
        this.currentSymbolsHeight.unshift(this.symbolsHeight[symbolId - 1]);
    }

    private bounceCounters(enable: boolean): void {
        let countersHolder = document.getElementById("jackpotCounters");
        let countersLogo = document.getElementById("jackpotLogo");

        if (countersHolder != null && countersLogo != null) {
            if (enable) {
                countersHolder.classList.add("jackpotBounce");
                countersLogo.classList.add("jackpotBounce");
            } else {
                countersHolder.classList.remove("jackpotBounce");
                countersLogo.classList.remove("jackpotBounce");
            }
        }
    }

    private getCustomContent(name: string, extension: string): Promise<Array<string>> {
        return new Promise<Array<string>>((resolve: Function, reject: Function) => {
            if (AppContext.gameConfiguration == null) {
                resolve([name, ""]);
                return;
            }

            if (AppContext.gameConfiguration.StaticContentUrl == "") {
                resolve([name, ""]);
                return;
            }

            let theme = AppContext.gameConfiguration.ProgressiveBonusTheme;
            if (theme == "") {
                if (AppContext.gameConfiguration.UseJackpotLocalization) {
                    theme = Jackpot.JackpotTheme;
                } else {
                    resolve([name, ""]);
                    return;
                }
            }

            const staticContentUrl = AppContext.gameConfiguration.StaticContentUrl;
            const spbFolder = "SynotProgressiveBonus";
            const url = `${staticContentUrl}${spbFolder}/${theme}/${window["QUALITY"]}/${name}${extension}`;
            let img = document.createElement("img");
            img.setAttribute("src", url);

            img.addEventListener("load", (ev) => {
                resolve([name, url]);
            });
            img.addEventListener("error", (ev) => {
                resolve([name, ""]);
            });
        });
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/Common/Jackpot/JackpotGame.ts
 **/